/*
  <collection-page>: filters and sort for collection and search results.
  Every change in the filter panel (or the sort list in the toolbar) fetches the section with the
  new filters through the Section Rendering API and swaps the results, the count, the active
  filters and the panel's own counts in place. The address bar follows, so Back works.
  Without JavaScript the same form submits normally.
*/
const Spectre = window.Spectre || {};

class CollectionPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    this.abort?.abort();
  }

  get form() {
    return this.querySelector('[data-region="facets"] [data-facets-form]');
  }

  query(form = this.form) {
    const params = new URLSearchParams();
    if (!form) return params.toString();
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim() !== '') params.append(key, value);
    }
    return params.toString();
  }

  onChange = (event) => {
    const target = event.target;
    if (target.matches('[data-facets-sort]')) {
      const radio = this.form?.querySelector(`input[name="sort_by"][value="${CSS.escape(target.value)}"]`);
      if (radio) radio.checked = true;
      this.update(this.query());
      return;
    }
    if (!target.closest('[data-region="facets"]') || target.matches('[data-facets-price]')) return;
    this.update(this.query());
  };

  /* Prices update after a short pause in typing */
  onInput = (event) => {
    if (!event.target.matches('[data-facets-price]')) return;
    window.clearTimeout(this.priceTimer);
    this.priceTimer = window.setTimeout(() => this.update(this.query()), 600);
  };

  onSubmit = (event) => {
    if (!event.target.matches('[data-facets-form]')) return;
    event.preventDefault();
    window.clearTimeout(this.priceTimer);
    this.update(this.query(event.target)).then(() => this.querySelector('spectre-drawer')?.close());
  };

  onClick = (event) => {
    const link = event.target.closest('[data-facets-link]');
    if (!link || !this.contains(link)) return;
    event.preventDefault();
    this.update(new URL(link.href, window.location.origin).searchParams.toString());
  };

  onPopState = () => {
    this.update(window.location.search.slice(1), false);
  };

  async update(query, push = true) {
    this.abort?.abort();
    this.abort = new AbortController();
    const path = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${path}${query ? '&' : '?'}section_id=${encodeURIComponent(this.sectionId)}`, {
        signal: this.abort.signal,
      });
      if (!response.ok) throw new Error(String(response.status));
      const source = Spectre.parseHTML(await response.text()).querySelector('collection-page');
      if (!source) return;

      const active = document.activeElement;
      const focusId = active && this.contains(active) ? active.id : null;
      Spectre.swapRegions(this, source, 'data-region');
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });

      if (push) window.history.pushState({ facets: true }, '', path);
      Spectre.announce?.(this.querySelector('[data-region="count"]')?.textContent?.trim());
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = path;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('collection-page')) customElements.define('collection-page', CollectionPage);
